import { EnquiryForm, PageBanner, Section, SectionTitle } from '@longnan/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage, useSiteData } from '../useSiteData';

export function Contact() {
  const { t, pick } = useT();
  const s = useSiteData();
  const error = s.error;
  if (error) return <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{error}</div>;
  if (!s.data) return <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>;

  const address = s.data.overview.identity.registeredAddress;
  const directions = ['media', 'scenic', 'health', 'tech', 'invest', 'rural', 'ir', 'other'];
  const label = (k: string) => ({ zh: t(k), en: t(k) });
  const img = findImage(s.data.imagery, 'convention');

  return (
    <>
      <PageBanner
        image={assetUrl(img.local)}
        imageAlt={pick(img.alt)}
        imageCredit={creditOf(img)}
        eyebrow={t('contact.eyebrow')}
        title={t('contact.title')}
        lead={t('contact.lead')}
      />

      <Section id="info">
        <div className="grid gap-12 md:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionTitle id="info" title={t('contact.address')} />
            <p className="mt-6 text-lg leading-relaxed text-body">{pick(address)}</p>
            <dl className="mt-6 grid gap-2 text-sm">
              <div className="flex flex-wrap gap-x-3">
                <dt className="text-muted">{t('legal.name')}</dt>
              </div>
              <div className="flex flex-wrap gap-x-3">
                <dt className="text-muted">{t('legal.taxLabel')}</dt>
                <dd className="tnum tracking-wide text-body">{t('legal.taxId')}</dd>
              </div>
            </dl>

            <h3 className="mt-12 text-sm font-semibold tracking-widest text-muted">{t('contact.channels')}</h3>
            <ul className="mt-4 divide-y divide-line border-y border-line">
              {s.data.company.contact.channels.map((c) => (
                <li key={c.id} className="py-4">
                  <div className="text-xs text-muted">{pick(c.label)}</div>
                  <div className="mt-1 text-base text-body">{pick(c.value)}</div>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-muted">{pick(s.data.company.contact.note)}</p>
          </div>

          <div>
            <SectionTitle id="form" title={t('contact.form.title')} />
            <div className="mt-8">
              <EnquiryForm
                idPrefix="contact"
                fields={[
                  { id: 'name', label: label('form.name'), type: 'text', required: true },
                  { id: 'company', label: label('form.company'), type: 'text' },
                  { id: 'email', label: label('form.email'), type: 'email', required: true },
                  {
                    id: 'direction',
                    label: label('form.direction'),
                    type: 'select',
                    required: true,
                    options: directions.map((d) => ({ value: d, label: label(`dir.${d}`) })),
                  },
                  { id: 'message', label: label('form.message'), type: 'textarea', required: true },
                ]}
                submit={label('form.submit')}
                success={label('contact.form.success')}
                note={label('form.note')}
                requiredHint={label('form.required')}
                selectPlaceholder={label('form.select')}
              />
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
